<template>
  <v-dialog v-model="dialog" max-width="1600" content-class="gps-map-dialog" persistent>
    <div ref="mapCanvas" class="map-canvas"></div>

    <div class="card">
      <div class="bg">
        <div class="label-block">
          <v-checkbox v-model="mapStyles" label="GPS Map Data" dense hide-details value="wayPoints" color="#0075ff" @change="togglePolyline('waypoints')"></v-checkbox>
          <i class="fas fa-circle gps-circle"></i>
        </div>
        <div class="label-block">
          <v-checkbox v-model="mapStyles" label="Estimated Route" dense hide-details value="direction" color="#0075ff" @change="togglePolyline('direction')"></v-checkbox>
          <div class="line-block">
            <div class="line"></div>
            <i class="fas fa-caret-right"></i>
          </div>
        </div>
        <div class="alert-block" v-if="hasLocations">
          <span><i class="fas fa-info-circle"></i> Click on the <i class="fas fa-circle gps-circle"></i> to view the time</span>
        </div>
        <div class="alert-block no-data" v-else-if="!loading">
          <span><i class="fas fa-info-circle"></i> {{ message || "No GPS data available for this trip" }}</span>
        </div>
      </div>
    </div>

    <v-btn class="close-button" color="#ffffff" @click="closeMap"><i class="far fa-times-circle"></i></v-btn>
  </v-dialog>
</template>

<script>
import axios from "@/axios/axios-global";
import LeafLet from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import "leaflet-arrowheads";
import PolylineUtil from "polyline-encoded";

const DEFAULT_CENTER = [12.9716, 77.5946];

export default {
  name: "GpsMapPath",
  props: {
    tripId: Number,
  },
  data() {
    return {
      dialog: true,
      loading: true,
      message: "",
      map: null,
      mapDetails: {},
      userMarkersGroup: null,
      polyLineLayer: null,
      mapStyles: ["wayPoints", "direction"],
    };
  },
  computed: {
    hasLocations() {
      return !!this.mapDetails.locationAndTime && this.mapDetails.locationAndTime.length > 0;
    },
  },
  async mounted() {
    await this.getGpsPath();
  },
  beforeDestroy() {
    this.removeMap();
  },
  methods: {
    async getGpsPath() {
      try {
        const response = await axios.post("trip/gps/polyline", { tripId: this.tripId });
        if (response.status === 200 && response.data && typeof response.data === "object") {
          this.mapDetails = response.data;
        } else {
          this.message = typeof response.data === "string" ? response.data : "";
        }
      } catch (error) {
        this.message = "Unable to load GPS data";
      }
      this.loading = false;

      await this.setUpMap();
      if (!this.map) return;
      if (this.hasLocations) this.addLocation();
      if (this.mapDetails.polyline) this.addPolyline();
      this.fitMap();
    },
    async setUpMap() {
      this.removeMap();
      await this.$nextTick();
      const mapElement = this.$refs.mapCanvas;
      if (!mapElement) return;

      this.map = LeafLet.map(mapElement, { zoomControl: false }).setView(DEFAULT_CENTER, 12);

      LeafLet.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        // OSM answers a tile request that carries no Referer with its "Access blocked" image.
        referrerPolicy: "strict-origin-when-cross-origin",
      }).addTo(this.map);

      LeafLet.control.zoom({ position: "bottomleft" }).addTo(this.map);

      // The dialog is still animating open when the map is created.
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.fitMap();
        }
      }, 300);
    },
    removeMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.userMarkersGroup = null;
      this.polyLineLayer = null;
    },
    togglePolyline(type) {
      if (!this.map) return;
      if (type === "waypoints") {
        if (this.userMarkersGroup) this.map.removeLayer(this.userMarkersGroup);
        this.userMarkersGroup = null;
        if (this.mapStyles.includes("wayPoints") && this.hasLocations) this.addLocation();
      } else {
        if (this.polyLineLayer) this.map.removeLayer(this.polyLineLayer);
        this.polyLineLayer = null;
        if (this.mapStyles.includes("direction") && this.mapDetails.polyline) this.addPolyline();
      }
    },
    closeMap() {
      this.removeMap();
      this.$emit("close-map");
    },
    addLocation() {
      this.userMarkersGroup = LeafLet.layerGroup().addTo(this.map);
      this.mapDetails.locationAndTime.forEach((location) => {
        const marker = LeafLet.marker([location.lat, location.lng], { icon: this.generateLocation() }).addTo(this.userMarkersGroup);
        marker.bindPopup(`<strong>${this.getLocationTime(location.time)}</strong>`);
      });
    },
    addPolyline() {
      const polyLine = PolylineUtil.decode(this.mapDetails.polyline, 5);
      if (polyLine.length === 0) return;

      const polyline = LeafLet.polyline(polyLine, {
        color: "#0b51dd",
        weight: 3,
        opacity: 1,
      }).arrowheads({ size: "15px", frequency: "50px", yawn: 40, fill: true });

      const startMarker = LeafLet.marker(polyLine[0], { icon: this.generateMarker("start") });
      const endMarker = LeafLet.marker(polyLine[polyLine.length - 1], { icon: this.generateMarker("end") });

      this.polyLineLayer = LeafLet.featureGroup([polyline, startMarker, endMarker]);
      this.polyLineLayer.addTo(this.map);
    },
    fitMap() {
      const points = [];
      if (this.hasLocations) {
        this.mapDetails.locationAndTime.forEach((location) => points.push([location.lat, location.lng]));
      }
      if (this.mapDetails.polyline) {
        PolylineUtil.decode(this.mapDetails.polyline, 5).forEach((point) => points.push(point));
      }
      if (points.length > 0) this.map.fitBounds(points, { padding: [30, 30] });
    },
    generateLocation() {
      const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#CC5500"><path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8z"/></svg>`;
      return LeafLet.divIcon({
        html: svgIcon,
        className: "custom-div-icon",
        iconSize: [15, 15],
      });
    },
    generateMarker(status) {
      const color = status === "start" ? "green" : "#C82333";
      return LeafLet.divIcon({
        html: `
    <div class="location-container" style="border: 2px solid ${color};">
      <div class="location-icon" style="background: ${color};">
        ${status}
      </div>
    </div>`,
        className: "custom-div-icon",
        iconSize: [30, 30],
      });
    },
    getLocationTime(time) {
      return moment(time).format("DD-MM-YYYY hh:mm A");
    },
  },
};
</script>

<!-- Not scoped: v-dialog moves its content out of this component, and Leaflet builds the markers itself. -->
<style lang="scss">
.gps-map-dialog {
  background-color: white;
  height: calc(100vh - 150px);
  position: relative;
  overflow: hidden;

  .map-canvas {
    height: 100%;
    width: 100%;
  }

  .card {
    position: absolute;
    top: 0;
    left: 0;
    width: 230px;
    z-index: 1001;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(24px);
    overflow: hidden;
    outline: 2px solid white;

    .bg {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 10px;

      .label-block {
        display: flex;
        align-items: center;
        padding: 8px;
        border-radius: 5px;
        box-shadow: rgba(0, 0, 0, 0.13) 0px 2px 4px 0px;
        justify-content: space-between;

        .gps-circle {
          color: #cc5500;
        }
        .line-block {
          display: flex;
          align-items: center;
          .line {
            width: 15px;
            height: 2px;
            background: #0b51dd;
          }
          .fa-caret-right {
            color: #0b51dd;
          }
        }
      }

      .alert-block {
        padding: 5px 10px;
        background: #ffeada;
        border-radius: 5px;
        span {
          font-size: 12px;
          color: #ff8b38;
        }
        .gps-circle {
          color: #cc5500;
        }
      }
      .no-data {
        background: #f1f1f1;
        span {
          color: #666;
        }
      }

      .v-input--selection-controls {
        margin-top: 0;
        padding-top: 0;
      }
      .v-label {
        font-weight: bold;
        font-size: 14px;
      }
    }
  }

  .close-button {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 50px;
    min-width: 50px;
    height: 40px;
    font-size: 1.5rem;
    box-shadow: none;
    color: red;
    z-index: 1001;
  }

  .location-container {
    border-radius: 50%;
    height: 50px;
    width: 50px;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    .location-icon {
      border: 2px solid white;
      border-radius: 50%;
      height: 40px;
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      text-transform: uppercase;
      padding: 5px;
      font-size: 10px;
    }
  }
}
</style>
